import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { MeetingForAction } from '../../interface';
import { Preferences, TeamEventMeetingType } from '@vendasta/meetings';
import { MeetingSchedulerStoreService } from '../../../data-providers/meeting-scheduler-store.service';
import { CalendarConfig } from '../../../shared/components/calendar-date-picker/calendar-date-picker.component';
import moment from 'moment';
import 'moment-timezone';

@Component({
  selector: 'meeting-scheduler-reschedule-meeting',
  templateUrl: './reschedule-meeting-dialog.component.html',
  styleUrls: ['./reschedule-meeting-dialog.component.scss'],
  standalone: false,
})
export class RescheduleMeetingDialogComponent implements OnInit, OnDestroy {
  rescheduleForm: UntypedFormGroup;
  hostPreferences$: Observable<Preferences>;
  subscription: Subscription[] = [];
  timezone = '';
  userId = '';
  availableTimeSlots: string[] = [];
  calendarConfig: CalendarConfig | null = null;
  selectedDate: Date | null = null;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly dialogRef: MatDialogRef<RescheduleMeetingDialogComponent>,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    @Inject(MAT_DIALOG_DATA) public meeting: MeetingForAction,
  ) {}

  ngOnInit(): void {
    this.userId = this.meetingSchedulerStoreService.getUserId();

    this.hostPreferences$ = this.meetingSchedulerStoreService.loadUserPreferences();
    this.subscription.push(
      this.hostPreferences$
        .pipe(
          filter((hostPreference) => !!hostPreference?.timezone?.id),
          distinctUntilChanged((prev, curr) => prev?.timezone?.id === curr?.timezone?.id),
        )
        .subscribe((hostPreference) => {
          this.timezone = hostPreference?.timezone?.id;
          this.setupCalendarConfig();
        }),
    );

    this.rescheduleForm = this.fb.group({
      meetingId: [this.meeting.meetingId],
      date: [null, Validators.required],
      timeSlot: [null, Validators.required],
      messageToGuest: [null],
    });
  }

  private setupCalendarConfig(): void {
    let userID = this.userId;
    if (this.meeting.meetingType === TeamEventMeetingType.MULTI_HOST) {
      userID = '';
    }

    this.calendarConfig = {
      calendarId: this.meeting.calendarId,
      meetingTypeId: this.meeting.eventTypeId,
      timezone: this.timezone,
      userID: userID,
    };
  }

  onDateChange(date: Date | null): void {
    this.selectedDate = date;
    this.rescheduleForm.get('date')!.setValue(date);
    this.rescheduleForm.get('timeSlot')!.setValue(null);
  }

  onAvailableTimeSlotsChange(timeSlots: string[]): void {
    this.availableTimeSlots = timeSlots;
  }

  ngOnDestroy() {
    this.subscription.forEach((sub) => sub.unsubscribe());
    this.subscription = [];
  }

  rescheduleMeeting(): void {
    // Combine the selected date and time into a proper Date object
    const selectedDate = this.rescheduleForm.value.date;
    const selectedTimeSlot = this.rescheduleForm.value.timeSlot;

    if (!selectedDate || !selectedTimeSlot) {
      console.error('Missing date or time slot for rescheduling');
      return;
    }

    const dateString = moment(selectedDate).format('YYYY-MM-DD');
    const dateTimeString = `${dateString} ${selectedTimeSlot}`;
    const localDateTime = moment.tz(dateTimeString, 'YYYY-MM-DD hh:mm A', this.timezone);
    const meetingStartTime = localDateTime.toDate();

    this.dialogRef.close({ meetingId: this.meeting.meetingId, start: meetingStartTime });
  }

  close(): void {
    this.dialogRef.close();
  }
}
